import { useMemo } from "react";
import { useSelector } from "react-redux";
import { getFleetMissions, getReleasedStops } from "Slices/operatorData";
import { getVehiclesDict } from "Slices/vehicles";
import { getVehiclesUpdateData } from "Slices/vehiclesState";
import { getVehicleTypes } from "Slices/vehicleTypes";
import {
  getOperatorPickedStation,
  getOperatorStations,
} from "Slices/operatorStation";
import { CONFIRMATION_TYPES } from "Components/common/constants";
import { getCurrentFleetId } from "Slices/missionPlanner";
import { ActionRequiredListItemData } from "../components/ActionRequiredListItem/ActionRequiredListItem";
import { resolveLiveBattery } from "../utils/liveBattery";

const CLOUD_CONFIRMATION_TYPES = new Set([
  CONFIRMATION_TYPES.CLOUD_HMI_RELEASE_CONFIRMATION,
  CONFIRMATION_TYPES.CLOUD_RELEASE_CONFIRMATION,
]);

const QUEUED_STATUSES = new Set(["NEW", "SENT", "RECEIVED"]);

// DEV-4157: vehicles below this battery percentage cannot be assigned missions,
// so the station view must not present them as "Ready for Work" — show a charge
// warning instead.
const MIN_BATTERY_PERCENT_FOR_DISPATCH = 30;
const LOW_BATTERY_STATUS_TEXT = "Charge Vehicle";
const LOW_BATTERY_MESSAGE = "Battery under 30%, please charge the vehicle.";

const isBatteryBelowDispatchThreshold = (batteryPercent: number): boolean =>
  batteryPercent < MIN_BATTERY_PERCENT_FOR_DISPATCH;

export const useActionRequiredVehicles = (): ActionRequiredListItemData[] => {
  const queuedMissions = useSelector(getFleetMissions);
  const vehiclesDict = useSelector(getVehiclesDict);
  // Live WS slice. DEV-3454 reads `battery_level` from it (via resolveLiveBattery);
  // DEV-3706 reads live online/offline status from it. Keyed by no-dash hex
  // (vehiclesDict uses dashed UUIDs) — match both formats like useInProgressVehicles.
  const vehiclesUpdateData = useSelector(getVehiclesUpdateData);
  const vehicleTypes = useSelector(getVehicleTypes);
  const operatorStations = useSelector(getOperatorStations);
  const pickedStation = useSelector(getOperatorPickedStation);
  const currentFleetId = useSelector(getCurrentFleetId);
  const releasedStops = useSelector(getReleasedStops);

  const selectedStationId = useMemo(() => {
    const station = operatorStations.find((each) => each.isPicked);
    return station?.id;
  }, [operatorStations]);

  const userStationId = pickedStation?.stationId;

  // Build a set of vehicle IDs that already have a mission queued up (NEW/SENT/RECEIVED).
  // When a vehicle at the station has a queued next mission, show Release; otherwise Dispatch.
  const vehiclesWithQueuedMission = useMemo(() => {
    if (!queuedMissions) return new Set<string>();
    return new Set(
      queuedMissions
        .filter((m) => QUEUED_STATUSES.has(m.status) && m.vehicle)
        .map((m) => m.vehicle)
    );
  }, [queuedMissions]);

  return useMemo(() => {
    if (!selectedStationId) return [];

    const results: ActionRequiredListItemData[] = [];
    const missions = queuedMissions ?? [];

    // DEV-3706: resolve a vehicle's live online status. vehiclesState is keyed
    // by no-dash hex while the static vehicles dict uses dashed UUIDs, so try
    // both (mirrors useInProgressVehicles). Returns true only when the live
    // signal explicitly reports OFFLINE — absence of data is treated as online
    // so we never hide/gate a vehicle just because the WS hasn't reported yet.
    const isVehicleOffline = (vehicleId: string): boolean => {
      if (!vehicleId || !vehiclesUpdateData) return false;
      const noDashId = vehicleId.replace(/-/g, "");
      const live =
        vehiclesUpdateData[noDashId] || vehiclesUpdateData[vehicleId];
      return live?.online_status === "OFFLINE";
    };

    for (const mission of missions) {
      if (mission.status !== "IN_PROGRESS") continue;

      const passedStops = mission.passedStops ?? 0;
      if (passedStops === 0) continue;

      const stops = mission.stops ?? [];
      const currentStop = stops[passedStops - 1];
      if (!currentStop) continue;

      const isManualRelease =
        currentStop.confirmation_type === CONFIRMATION_TYPES.MANUAL_RELEASE;
      const isCloudRelease = CLOUD_CONFIRMATION_TYPES.has(
        currentStop.confirmation_type
      );
      if (!isManualRelease && !isCloudRelease) continue;

      if (currentStop.original_stop_id !== selectedStationId) continue;

      // Hide once the operator has released this stop — the vehicle is now
      // in motion and should appear in the In Progress tab instead.
      if (releasedStops[mission.id] === passedStops) continue;

      const vehicleId = mission.vehicle;
      const vehicle = vehicleId ? vehiclesDict?.[vehicleId] : undefined;
      const vehicleType = vehicle
        ? vehicleTypes?.find((vt) => vt.id === vehicle.vehicleTypeId)
        : null;
      // DEV-3454 + DEV-4157: use the live WS battery (falls back to the cached
      // vehicle.batteryLevel) so the displayed value and the sub-30% dispatch
      // gate below agree.
      const batteryPercent = resolveLiveBattery(
        vehicleId,
        vehicle?.batteryLevel,
        vehiclesUpdateData
      );

      const hasQueuedMission = vehicleId
        ? vehiclesWithQueuedMission.has(vehicleId)
        : false;

      // Sibling queued mission for this vehicle that's targeted at the picked
      // station. When present, the operator's "next" dispatch is blocked behind
      // this MANUAL_RELEASE — surface its name in the row instead of the
      // (now-stale) blocker mission's name like "Call Vehicle to <stop>".
      const queuedSiblingForStation = vehicleId
        ? missions.find(
            (m) =>
              QUEUED_STATUSES.has(m.status) &&
              m.vehicle === vehicleId &&
              m.stationStopId === selectedStationId
          )
        : undefined;

      // Through-stop: vehicle is mid-mission with more stops ahead (e.g. a loop
      // passing through the station). Always show Release — there's no dispatch needed.
      const isThroughStop = stops.length > passedStops;

      let missionText: string;
      let buttonType: ActionRequiredListItemData["buttonType"];
      let buttonText: string;

      if (!isThroughStop && !hasQueuedMission) {
        missionText = "Ready for Work";
        buttonType = "dispatch";
        buttonText = "Dispatch";
      } else if (isManualRelease) {
        missionText = isThroughStop
          ? mission.name ?? "On Mission"
          : "Manual Release Required";
        buttonType = "dispatch";
        buttonText = "Dispatch";
      } else {
        missionText = isThroughStop
          ? mission.name ?? "On Mission"
          : "Awaiting Input";
        buttonType = "release";
        buttonText = "Release Vehicle";
      }

      let missionName: string | undefined;
      if (!isThroughStop && isManualRelease && queuedSiblingForStation) {
        const stopName = currentStop.name ?? "this stop";
        const queuedName = queuedSiblingForStation.name ?? "Queued mission";
        missionName = `Parked at ${stopName} — ${queuedName} queued`;
      } else if (!isThroughStop && mission.name) {
        missionName = mission.name;
      }

      // DEV-4157: a vehicle that is otherwise "Ready for Work" but below the
      // mission-assignment battery threshold cannot be dispatched — replace the
      // misleading status with a charge warning. Runs before the offline gate so
      // an offline low-battery vehicle still surfaces as "Offline" (offline wins).
      const lowBattery =
        missionText === "Ready for Work" &&
        isBatteryBelowDispatchThreshold(batteryPercent);
      if (lowBattery) {
        missionText = LOW_BATTERY_STATUS_TEXT;
        missionName = LOW_BATTERY_MESSAGE;
      }

      // DEV-3706: an offline vehicle can't be dispatched (backend DEV-3686
      // rejects it). Surface "Offline", grey the badge (delivery status), and
      // flag the row so the dispatch button renders disabled. Release rows are
      // untouched — releasing a parked vehicle is a separate action.
      const offline = isVehicleOffline(vehicleId || "");
      const rowMissionText =
        offline && buttonType === "dispatch" ? "Offline" : missionText;
      const rowMissionStatus =
        offline && buttonType === "dispatch"
          ? "delivery"
          : isThroughStop || hasQueuedMission
          ? "delivery"
          : "ready";

      results.push({
        id: mission.id,
        name: vehicle?.name || vehicleId || "",
        imageUrl: vehicleType?.image?.link,
        // DEV-3454: live battery (resolved once into batteryPercent above).
        // DEV-3706: offline-gated status/text. DEV-4157: low-battery flag/text.
        batteryPercent,
        missionStatus: rowMissionStatus,
        missionText: rowMissionText,
        missionName,
        lowBattery,
        buttonType,
        buttonText,
        queuedMissionId: mission.id,
        vehicleId: vehicleId || "",
        stationId: userStationId,
        fleetId: currentFleetId ?? undefined,
        isOffline: offline && buttonType === "dispatch",
        // Raw offline state (independent of buttonType) so the component can
        // also gate the secondary "Change mission" re-dispatch button that a
        // release row renders — the primary "Release Vehicle" action stays live.
        vehicleOffline: offline,
      });
    }

    // Second pass — surface a "Manual Release Required" row on the dispatch
    // station when the operator's queued mission is blocked behind a sibling
    // mission parked at a MANUAL_RELEASE stop elsewhere. Without this the
    // operator sees no indication of why their dispatch isn't moving. Dedup
    // is vehicle-keyed: when the first pass already emitted a row for this
    // vehicle (blocker is at the picked station), don't add another.
    const emittedVehicleIds = new Set(
      results.map((r) => r.vehicleId).filter(Boolean)
    );
    for (const queued of missions) {
      if (!QUEUED_STATUSES.has(queued.status)) continue;
      if (queued.stationStopId !== selectedStationId) continue;
      const queuedVehicleId = queued.vehicle;
      if (!queuedVehicleId) continue;
      if (emittedVehicleIds.has(queuedVehicleId)) continue;

      const blocker = missions.find((m) => {
        if (m.vehicle !== queuedVehicleId) return false;
        if (m.status !== "IN_PROGRESS") return false;
        const passed = m.passedStops ?? 0;
        if (passed <= 0) return false;
        const cur = m.stops?.[passed - 1];
        return (
          !!cur && cur.confirmation_type === CONFIRMATION_TYPES.MANUAL_RELEASE
        );
      });
      if (!blocker) continue;

      const blockerCurrentStop =
        blocker.stops?.[(blocker.passedStops ?? 0) - 1];
      const queuedVehicle = vehiclesDict?.[queuedVehicleId];
      const queuedVehicleType = queuedVehicle
        ? vehicleTypes?.find((vt) => vt.id === queuedVehicle.vehicleTypeId)
        : null;
      const blockerStopName = blockerCurrentStop?.name ?? "another stop";
      const queuedName = queued.name ?? "Queued mission";

      // DEV-3706: this is a dispatch row too — gate it on the queued vehicle's
      // live online status so an offline vehicle's "Dispatch" button is
      // disabled here exactly as in the first pass.
      const blockedOffline = isVehicleOffline(queuedVehicleId);

      results.push({
        id: `${queued.id}-blocked`,
        name: queuedVehicle?.name || queuedVehicleId,
        imageUrl: queuedVehicleType?.image?.link,
        batteryPercent: resolveLiveBattery(
          queuedVehicleId,
          queuedVehicle?.batteryLevel,
          vehiclesUpdateData
        ),
        missionStatus: "delivery",
        missionText: blockedOffline ? "Offline" : "Manual Release Required",
        missionName: `Parked at ${blockerStopName} — ${queuedName} queued`,
        buttonType: "dispatch",
        buttonText: "Dispatch",
        queuedMissionId: queued.id,
        vehicleId: queuedVehicleId,
        stationId: userStationId,
        fleetId: currentFleetId ?? undefined,
        isOffline: blockedOffline,
        vehicleOffline: blockedOffline,
      });
      emittedVehicleIds.add(queuedVehicleId);
    }

    // Third pass — idle vehicles physically near the picked station. A vehicle
    // with no active/queued mission, parked within a small radius of the
    // station, should appear as "Ready for Work" so the operator can dispatch
    // it without first walking outside or refreshing the fleet view.
    const IDLE_PROXIMITY_RADIUS_METERS = 2;
    const stationLat = pickedStation?.latitude;
    const stationLng = pickedStation?.longitude;
    if (
      vehiclesDict &&
      Number.isFinite(stationLat) &&
      Number.isFinite(stationLng)
    ) {
      // Vehicle IDs that already have ANY non-terminal mission — skip those.
      const vehiclesWithAnyActiveMission = new Set<string>();
      for (const m of missions) {
        if (!m.vehicle) continue;
        if (m.status === "IN_PROGRESS" || QUEUED_STATUSES.has(m.status)) {
          vehiclesWithAnyActiveMission.add(m.vehicle);
        }
      }

      const R = 6371008.8;
      const toRad = (deg: number) => (deg * Math.PI) / 180;
      const lat1 = toRad(stationLat as number);
      const cosLat1 = Math.cos(lat1);

      for (const vehicleId of Object.keys(vehiclesDict)) {
        if (emittedVehicleIds.has(vehicleId)) continue;
        if (vehiclesWithAnyActiveMission.has(vehicleId)) continue;
        // DEV-3706: don't surface an offline idle vehicle as "Ready for Work" —
        // it can't accept a dispatch. Drop it from the list entirely.
        if (isVehicleOffline(vehicleId)) continue;
        const v = vehiclesDict[vehicleId];
        if (!v) continue;
        if (currentFleetId && v.fleetId && v.fleetId !== currentFleetId) {
          continue;
        }
        if (!Number.isFinite(v.latitude) || !Number.isFinite(v.longitude)) {
          continue;
        }
        const lat2 = toRad(v.latitude);
        const dLat = lat2 - lat1;
        const dLng = toRad(v.longitude - (stationLng as number));
        const sinDLat = Math.sin(dLat / 2);
        const sinDLng = Math.sin(dLng / 2);
        const h =
          sinDLat * sinDLat + cosLat1 * Math.cos(lat2) * sinDLng * sinDLng;
        const distance = 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
        if (distance > IDLE_PROXIMITY_RADIUS_METERS) continue;

        const vt = vehicleTypes?.find((t) => t.id === v.vehicleTypeId);
        // DEV-3454 + DEV-4157: live battery so the displayed value and the
        // sub-30% gate agree (mirrors the queued-mission pass above).
        const idleBatteryPercent = resolveLiveBattery(
          vehicleId,
          v.batteryLevel,
          vehiclesUpdateData
        );
        const idleLowBattery =
          isBatteryBelowDispatchThreshold(idleBatteryPercent);
        results.push({
          id: `idle-${vehicleId}`,
          name: v.name || vehicleId,
          imageUrl: vt?.image?.link,
          batteryPercent: idleBatteryPercent,
          missionStatus: "ready",
          missionText: idleLowBattery
            ? LOW_BATTERY_STATUS_TEXT
            : "Ready for Work",
          missionName: idleLowBattery ? LOW_BATTERY_MESSAGE : undefined,
          lowBattery: idleLowBattery,
          buttonType: "dispatch",
          buttonText: "Dispatch",
          queuedMissionId: "",
          vehicleId,
          stationId: userStationId,
          fleetId: currentFleetId ?? undefined,
          isOffline: false,
          // Offline idle vehicles are dropped above, so any idle row that
          // reaches here is for an online vehicle.
          vehicleOffline: false,
        });
        emittedVehicleIds.add(vehicleId);
      }
    }

    return results;
  }, [
    queuedMissions,
    vehiclesDict,
    vehiclesUpdateData,
    vehicleTypes,
    selectedStationId,
    userStationId,
    currentFleetId,
    vehiclesWithQueuedMission,
    releasedStops,
    pickedStation,
  ]);
};
